import type { Primitive } from '@sentry/core';
import * as Sentry from '@sentry/react';
import { type ReactNode } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { SimpleCard } from '@hivebrite/design-system/SimpleCard';

const GenericErrorBanner: Sentry.FallbackRender = ({ resetError }) => (
  <Box
    maxWidth="920px"
    margin="auto"
    justifyContent="center"
    flexDirection="column"
    py={4}
  >
    <SimpleCard
      cardProps={{ width: '100%' }}
      title={s__(
        'ErrorBoundary|Sorry, something went wrong. Our team has been notified.',
      )}
      faIconProps={{
        iconName: 'user-robot-xmarks',
      }}
      description={
        <Box mt={4} flexDirection="column" font="h4-regular">
          {s__('ErrorBoundary|Here is what you can do:')}

          <Box pt={4} justifyContent="center">
            <Button
              onClick={() => {
                resetError();
              }}
              mr={1}
            >
              {s__('ErrorBoundary|Reload the page')}
            </Button>
          </Box>
        </Box>
      }
    />
  </Box>
);

interface ErrorBoundaryProps {
  children: ReactNode;
  context?: {
    tags?: { [key: string]: Primitive };
  };
  tribe?:
    | 'builder-tribe'
    | 'evolution-tribe'
    | 'user-journey-tribe'
    | 'admin-xp-squad';
  app?: 'fo' | 'bo' | 'po';
  module?: string;
  shouldReloadPageWhenResettingError?: boolean;
  submodule?: string;
}

const ErrorBoundary = ({
  children,
  context,
  tribe,
  app,
  module,
  shouldReloadPageWhenResettingError = false,
  submodule,
}: ErrorBoundaryProps) => (
  <Sentry.ErrorBoundary
    beforeCapture={(scope) => {
      if (context?.tags) {
        scope.setTags(context.tags);
      }

      if (tribe) {
        scope.setTag('tribe', tribe);
      }

      if (app) {
        scope.setTag('app', app);
      }

      if (module) {
        scope.setTag('module', module);
      }

      if (submodule) {
        scope.setTag('submodule', submodule);
      }

      scope.setTag('page_locale', I18n.locale);
    }}
    fallback={(props) => (
      <GenericErrorBanner
        {...props}
        resetError={() => {
          if (shouldReloadPageWhenResettingError) {
            window.location.reload();
          } else {
            props.resetError();
          }
        }}
      />
    )}
  >
    {children}
  </Sentry.ErrorBoundary>
);

export default ErrorBoundary;

type TribeErrorBoundary = Omit<ErrorBoundaryProps, 'tribe'>;

export const EvErrorBoundary = (props: TribeErrorBoundary) => (
  <ErrorBoundary {...props} tribe="evolution-tribe" />
);

export const UjErrorBoundary = (props: TribeErrorBoundary) => (
  <ErrorBoundary {...props} tribe="user-journey-tribe" />
);

export const BuilderErrorBoundary = (props: TribeErrorBoundary) => (
  <ErrorBoundary {...props} tribe="builder-tribe" />
);

export const AdminXpErrorBoundary = (props: TribeErrorBoundary) => (
  <ErrorBoundary {...props} tribe="admin-xp-squad" />
);
